import {
  Activity,
  ArrowLeftRight,
  Banknote,
  Bell,
  Bitcoin,
  Bot,
  Briefcase,
  Building2,
  CandlestickChart,
  Coins,
  CreditCard,
  Database,
  FileCheck,
  FileSearch,
  FileSpreadsheet,
  FileText,
  FileWarning,
  Globe,
  HandCoins,
  Home,
  Hourglass,
  Landmark,
  type LucideIcon,
  Megaphone,
  Network,
  Percent,
  Radar,
  Receipt,
  Repeat,
  Server,
  ShieldAlert,
  ShieldCheck,
  Tags,
  TrendingUp,
  UserCheck,
  UserSearch,
  Users,
  Wallet,
  Zap,
} from 'lucide-react';
import { config } from '@/lib/env';

export interface NavItem {
  title: string;
  to: string;
  icon: LucideIcon;
  /** Item is hidden from the sidebar (route still exists) when false */
  enabled?: boolean;
  /** Used by the mobile bottom nav, where `title` would truncate on narrow screens */
  shortTitle?: string;
}

export interface NavGroup {
  title: string;
  icon: LucideIcon;
  items: NavItem[];
}

export const HOME_NAV_ITEM: NavItem = { title: 'Home', to: '/', icon: Home };

// Mirrors the legacy BopNavigation tree (bop-ui components/BopNavigation.vue)
export const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Compliance',
    icon: ShieldCheck,
    items: [
      { title: 'Institutions', to: '/compliance/institutions', icon: Building2 },
      { title: 'Jurisdictions', to: '/compliance/jurisdictions', icon: Globe },
      { title: 'Terms and Conditions', to: '/compliance/terms-and-conditions', icon: FileText },
      { title: 'Onboarding Reviews', to: '/compliance/onboarding-reviews', icon: UserCheck },
      { title: 'Whitelisted Payout Addresses', to: '/compliance/whitelisted-payout-addresses', icon: Wallet },
      { title: 'Reports', to: '/compliance/reports', icon: FileSpreadsheet },
      { title: 'Market Surveillance', to: '/compliance/market-surveillance', icon: Radar },
      { title: 'Abuse Monitoring', to: '/compliance/abuse', icon: ShieldAlert },
      { title: 'FIU UTR Filing', to: '/compliance/fiu-utr', icon: FileWarning },
      { title: 'DAC8', to: '/compliance/dac8', icon: FileSearch },
    ],
  },
  {
    title: 'Operations',
    icon: Briefcase,
    items: [
      { title: 'Credit Lines', to: '/operations/credit-lines', icon: Banknote },
      { title: 'Fees', to: '/operations/fees', icon: Percent },
      { title: 'Fee Collection', to: '/operations/fee-runs', icon: Receipt },
      { title: 'Markets', to: '/operations/markets', icon: CandlestickChart },
      { title: 'Parked Deposits', to: '/operations/parked-deposits', icon: Hourglass },
      { title: 'Payout Accounts', to: '/operations/payout-accounts', icon: CreditCard },
      { title: 'Trading', to: '/operations/trading', icon: TrendingUp },
      { title: 'Transactions', to: '/operations/transactions', icon: ArrowLeftRight },
      { title: 'User Search', to: '/operations/user-search', icon: UserSearch, shortTitle: 'Users' },
      { title: 'User References', to: '/operations/user-references', icon: Users },
      { title: 'User Audit', to: '/operations/user-audit', icon: FileSearch },
      { title: 'Client Categorisation', to: '/operations/client-categorisation', icon: Tags },
      { title: 'Brokers', to: '/operations/brokers', icon: Network },
      { title: 'Market Maker', to: '/operations/market-maker', icon: Bot, enabled: config.MARKET_MAKER_ENABLED },
      { title: 'MM Programs', to: '/operations/mm-programs', icon: HandCoins },
    ],
  },
  {
    title: 'Communications',
    icon: Megaphone,
    items: [{ title: 'User Notifications', to: '/communications/user-notifications', icon: Bell }],
  },
  {
    title: 'Risk',
    icon: ShieldAlert,
    items: [
      { title: 'Assets', to: '/risk/assets', icon: Bitcoin },
      { title: 'Funding Fees', to: '/risk/fees', icon: Coins },
      { title: 'Instant Trade', to: '/instant/dashboard', icon: Zap },
      { title: 'FX Swaps', to: '/risk/fx-swaps', icon: Repeat },
    ],
  },
  {
    title: 'Treasury',
    icon: Landmark,
    items: [
      { title: 'Asset flows', to: '/treasury/flows/dashboard', icon: Coins },
      { title: 'Frick Balances', to: '/treasury/frick/balances', icon: Wallet },
      { title: 'Frick Reconciliation', to: '/treasury/frick/reconciliation', icon: FileCheck },
      { title: 'JPM Kinexys Accounts', to: '/treasury/kx/accounts', icon: Landmark },
    ],
  },
  {
    title: 'DevOps',
    icon: Server,
    items: [
      { title: 'Exchange', to: '/devops/exchange', icon: Activity },
      { title: 'QuestDB', to: '/devops/questdb', icon: Database },
    ],
  },
];

export const isNavItemEnabled = (item: NavItem) => item.enabled !== false;

export const isNavItemActive = (pathname: string, to: string) => pathname === to || pathname.startsWith(`${to}/`);

const MOBILE_PRIMARY_PATHS = ['/operations/user-search', '/operations/transactions', '/operations/markets'];

export const getMobilePrimaryNavItems = (groups: NavGroup[] = NAV_GROUPS): NavItem[] => {
  const byPath = new Map(groups.flatMap((group) => group.items).map((item) => [item.to, item]));
  const shortcuts = MOBILE_PRIMARY_PATHS.map((to) => byPath.get(to)).filter((item): item is NavItem => item !== undefined && isNavItemEnabled(item));

  return [HOME_NAV_ITEM, ...shortcuts];
};
